import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { StyledVideo } from './styles';

const GESTURE_EVENTS: (keyof DocumentEventMap)[] = ['click', 'touchstart', 'keydown'];
// Backoff schedule for re-attempting setMediaView when Safari attaches the
// stream but doesn't render frames — most commonly when the remote
// participant's audio track was disabled at the moment of attach.
const REATTACH_DELAYS_MS = [600, 1500, 3000, 5000];

export const Video = ({ participant, muted }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Safari blocks autoplay of unmuted media without recent user activation,
  // and additionally won't autoplay a remote stream whose audio track is
  // currently muted/disabled. Keep the tile muted until the first user
  // gesture, then unmute so the guest hears the host. Local participants
  // are always muted (avoid echo).
  const [hasUserGesture, setHasUserGesture] = useState(false);
  const wantsMuted = muted || !hasUserGesture;
  // Mirror the latest `wantsMuted` so async callbacks (the reattach timers,
  // in-flight `setMediaView` continuations) always read the current value
  // without re-running the whole attach effect when only the muted state
  // changes.
  const wantsMutedRef = useRef(wantsMuted);
  wantsMutedRef.current = wantsMuted;
  // The Sendbird statefy layer rebuilds the participant wrapper on every
  // remoteAudio/VideoSettingsChanged event (see statefy.tsx). Pinning the
  // attach effect to that reference made it tear down and restart on every
  // mute toggle, which cleared the reattach timer before it could fire.
  // Pin the effect to the stable participantId and read the latest wrapper
  // through a ref so retries still call the up-to-date setMediaView.
  const participantRef = useRef(participant);
  participantRef.current = participant;

  useEffect(() => {
    if (muted || hasUserGesture) return;
    const onGesture = () => {
      setHasUserGesture(true);
      // Also flip the element imperatively in case React hasn't re-rendered
      // yet — Safari needs the property change, not just the attribute.
      const el = videoRef.current;
      if (el) el.muted = false;
    };
    GESTURE_EVENTS.forEach((evt) =>
      document.addEventListener(evt, onGesture, { once: true })
    );
    return () => {
      GESTURE_EVENTS.forEach((evt) => document.removeEventListener(evt, onGesture));
    };
  }, [muted, hasUserGesture]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    let cancelled = false;
    const reattachTimers: ReturnType<typeof setTimeout>[] = [];

    const attachAndPlay = async () => {
      // Safari evaluates autoplay eligibility when srcObject is assigned.
      // `el.muted` must be set as a *property* before setMediaView assigns
      // the stream; otherwise Safari blocks autoplay whenever the remote
      // stream's audio track is disabled (host is muted) and never paints
      // a frame. The JSX `muted` prop is not reliable for this on Safari.
      el.muted = wantsMutedRef.current;
      el.playsInline = true;
      try {
        await participantRef.current.setMediaView(el);
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'sendbird-calls',
          level: 'warning',
          message: `setMediaView failed: ${err?.name || 'Error'} - ${err?.message || ''}`,
        });
        return;
      }
      if (cancelled) return;
      // Re-assert muted in case srcObject reassignment cleared the flag.
      el.muted = wantsMutedRef.current;
      try {
        await el.play?.();
      } catch {
        // Should not happen with muted=true; keep a catch for transient SDK
        // races so an unhandled rejection doesn't bubble up.
      }
    };

    attachAndPlay();

    // Safari sometimes attaches the stream but doesn't render frames until
    // a track event kicks the renderer (especially when the host's audio
    // track was already disabled at attach time). Re-attach with backoff
    // until video dimensions appear.
    REATTACH_DELAYS_MS.forEach((delay) => {
      reattachTimers.push(
        setTimeout(() => {
          if (cancelled || el.videoWidth > 0) return;
          attachAndPlay();
        }, delay)
      );
    });

    return () => {
      cancelled = true;
      reattachTimers.forEach(clearTimeout);
    };
  }, [participant.participantId]);

  return (
    <StyledVideo
      autoPlay
      playsInline
      muted={wantsMuted}
      ref={videoRef}
    />
  );
};
